/**
 * SenderDashboard — Bento grid of quick actions + active shipments preview.
 * Mirrors Flutter `home_page.dart` (sender branch).
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Send, Truck, MapPin, Building2, Package, Container } from 'lucide-react';
import { useMyShipments } from '@/hooks/useShipments';
import { AppLayout } from '@/components/layout/AppLayout';
import { ShipmentCard } from '@/components/shipments/ShipmentCard';

const TILES = [
  { to: '/shipments/create',  icon: Send,      label: 'إرسال شحنة',         bg: '#0F2A5C', fg: '#FFFFFF' },
  { to: '/trips/browse',      icon: Truck,     label: 'تصفّح الرحلات',        bg: '#F15A29', fg: '#FFFFFF' },
  { to: '/containers/browse', icon: Container, label: 'حاويات دولية',         bg: '#7C3AED', fg: '#FFFFFF' },
  { to: '/containers/my',     icon: Package,   label: 'مشاركاتي في الحاويات', bg: '#DC2626', fg: '#FFFFFF' },
  { to: '/track',             icon: MapPin,    label: 'تتبّع شحنة',           bg: '#16A34A', fg: '#FFFFFF' },
  { to: '/centers',           icon: Building2, label: 'أقرب مركز',           bg: '#2E6B9E', fg: '#FFFFFF' },
];

export default function SenderDashboard() {
  const { t } = useTranslation();
  const { data: shipments, isLoading } = useMyShipments('sender');
  const active = (shipments || []).filter(
    (s) => !['delivered', 'cancelled', 'returned'].includes(s.status),
  ).slice(0, 4);

  return (
    <AppLayout>
      {/* Bento grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {TILES.map((tile) => (
          <Link
            key={tile.to}
            to={tile.to}
            className="rounded-2xl p-5 flex flex-col items-start gap-3 hover:opacity-90 transition shadow-sm"
            style={{ background: tile.bg, color: tile.fg }}
          >
            <tile.icon size={28} />
            <span className="font-bold text-sm md:text-base">{t(`tile.${tile.to}`, tile.label)}</span>
          </Link>
        ))}
      </div>

      {/* Active shipments preview */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
            <Package size={18} /> {t('active_shipments', 'الشحنات النشطة')}
          </h2>
          <Link to="/shipments" className="text-primary text-sm hover:underline">
            {t('view_all', 'عرض الكل')}
          </Link>
        </div>
        {isLoading ? (
          <div className="bg-surface border border-border rounded-xl p-8 text-center text-text-tertiary">
            {t('loading', 'جاري التحميل...')}
          </div>
        ) : active.length === 0 ? (
          <div className="bg-surface border border-border rounded-xl p-8 text-center">
            <Package size={36} className="mx-auto text-text-tertiary mb-2" />
            <p className="text-text-secondary mb-3">{t('no_active_shipments', 'لا توجد شحنات نشطة')}</p>
            <Link
              to="/shipments/create"
              className="inline-block bg-primary text-white rounded-xl px-5 py-2.5 text-sm font-semibold"
            >
              {t('create_first_shipment', 'أنشئ أول شحنة')}
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {active.map((s) => (
              <ShipmentCard key={s.id} shipment={s} />
            ))}
          </div>
        )}
      </section>
    </AppLayout>
  );
}
